<template>
    <div class="hotspot-answer-box border-ui-style">
        <v-stage class="hotspot-select-screen w-full h-full overflow-hidden" ref="stage" :config="stageSize">
            <v-layer ref="layer">
                <v-image
                    :config="{
                        image: image,
                        width: stageSize.width,
                        height: stageSize.height
                    }"
                />
                <v-group
                    v-for="item in question.answers"
                    :key="item.id"
                    :config="item.group"
                    :ref="item.id"
                    @click="isInteract ? handleClickSelectAnswer(item.initIndex) : false"
                >
                    <v-rect v-if="!item.type || item.type == 'rect'" :config="item.rectangles" />
                    <v-circle v-if="item.type == 'circle'" :config="item.rectangles" />
                </v-group>
            </v-layer>
        </v-stage>
    </div>
</template>

<script>
import helperMixins from '@/mixins/helper.js'
import PinchZoom from 'pinch-zoom-js'

export default {
    props: {
        question: {
            type: Object
        },

        showCorrect: {
            type: Boolean,
            default: false
        },

        showAllChoices: {
            type: Boolean,
            default: true
        },

        isMaster: {
            type: Boolean,
            default: false
        },

        isInteract: {
            type: Boolean,
            default: true
        },

        isMultiple: {
            type: Boolean,
            default: false
        }
    },

    mixins: [helperMixins],

    data() {
        return {
            stageSize: {
                width: 500,
                height: 500
            },
            image: null,
            dragging: false,
            answerSelects: [],
            answerCounts: 0
        }
    },

    created() {
        const image = new window.Image()
        image.src = this.question.image
        image.crossOrigin = '*'
        image.onload = async () => {
            this.image = await this.renderHotspotImage(image)
        }
    },

    mounted() {
        this.genAnswers()
        this.initPinchZoom()
        if (this.isInteract) {
            this.$nextTick(() => {
                for (let i = 0; i < this.question.answers.length; i++) {
                    const rect = this.$refs[this.question.answers[i].id][0].getNode()
                    // Mobile only
                    rect.on('tap', () => {
                        this.handleClickSelectAnswer(this.question.answers[i].initIndex)
                    })
                }
            })
        }

        this.fitStageIntoParentContainer(this.stageSize)

        window.addEventListener('resize', () => {
            this.fitStageIntoParentContainer(this.stageSize)
        })
    },

    methods: {
        handleClickSelectAnswer(answerIndex) {
            if (
                (this.isMultiple && this.answerCounts >= this.question.answers.filter(e => e.is_correct).length) ||
                (!this.isMultiple && this.answerCounts >= 1)
            )
                return // prevent repeat click

            if (!this.dragging) {
                const answerSelect = this.question.answers.find(e => e.initIndex === answerIndex)

                if (this.answerSelects.length < 1 || !this.answerSelects.find(e => e.id == answerSelect.id)) {
                    answerSelect.rectangles.stroke = 'rgba(255, 255, 255, .5)'
                    answerSelect.rectangles.opacity = 1
                    this.answerSelects.push(answerSelect)
                    this.answerCounts += 1
                } else {
                    answerSelect.rectangles.stroke = 'transparent'
                    answerSelect.rectangles.opacity = 0
                    this.answerSelects.splice(
                        this.answerSelects.indexOf(e => e === answerSelect),
                        1
                    )
                    this.answerCounts -= 1
                }

                document.querySelector('.hotspot-answer-box').style.backgroundImage = `url(${this.question.image})`
                this.$emit('selectAnswer', answerIndex)
            }
        },

        initPinchZoom() {
            const el = document.querySelector('.hotspot-select-screen')
            const options = {
                draggableUnzoomed: false,
                minZoom: 1,
                onDragStart: () => {
                    this.dragging = true
                },
                onDragEnd: () => {
                    this.dragging = false
                },
                onZoomStart: () => {
                    this.dragging = true
                },
                onZoomEnd: () => {
                    this.dragging = false
                },
                onDragUpdate: e => {
                    if (e.lastScale < 1) {
                        e.lastScale = 1
                        e.offset.x = 0
                        e.offset.y = 0
                    }
                }
            }

            new PinchZoom(el, options)
        },

        hoverRect(e) {
            const name = e.target.name()
            const rect = this.question.answers.find(r => r.rectangles.name === name)
            const otherRects = this.question.answers.filter(e => e.rectangles.name !== name)

            for (let i = 0; i < otherRects.length; i++) {
                otherRects[i].rectangles.stroke = 'rgba(255, 255, 255, .5)'
            }

            if (!rect) return

            rect.rectangles.stroke = 'rgba(255, 255, 255, 1)'
        },

        async genAnswers() {
            if (this.showCorrect) {
                if (this.showAllChoices) {
                    this.question.answers = this.question.answers.map(e => {
                        return {
                            ...e,
                            group: {
                                ...e.group,
                                draggable: false
                            },
                            rectangles: {
                                ...e.rectangles,
                                fill: e.is_correct ? 'rgba(40, 167, 69, .3)' : 'transparent',
                                strokeWidth: 2,
                                stroke: e.is_correct ? 'green' : 'rgba(255, 255, 255, .5)'
                            }
                        }
                    })
                } else {
                    this.question.answers = this.question.answers.map(e => {
                        return {
                            ...e,
                            group: {
                                ...e.group,
                                draggable: false
                            },
                            rectangles: {
                                ...e.rectangles,
                                fill: e.is_correct ? 'rgba(40, 167, 69, .3)' : 'transparent',
                                strokeWidth: 2,
                                stroke: e.is_correct ? 'green' : 'transparent',
                                opacity: e.is_correct ? 1 : 0
                            }
                        }
                    })
                }
            } else {
                if (this.isMaster) {
                    this.question.answers = this.question.answers.map(e => {
                        return {
                            ...e,
                            group: {
                                ...e.group,
                                draggable: false
                            },
                            rectangles: {
                                ...e.rectangles,
                                fill: 'transparent',
                                strokeWidth: 2,
                                stroke: 'rgba(255, 255, 255, .5)',
                                opacity: 1
                            }
                        }
                    })
                } else {
                    this.question.answers = this.question.answers.map(e => {
                        return {
                            ...e,
                            group: {
                                ...e.group,
                                draggable: false
                            },
                            rectangles: {
                                ...e.rectangles,
                                fill: 'transparent',
                                strokeWidth: 2,
                                stroke: 'transparent',
                                opacity: 0
                            }
                        }
                    })
                }
            }
        }
    }
}
</script>

<style lang="scss">
.hotspot-answer-box {
    position: relative;
    width: 100%;
    height: auto;
    margin: 0 auto;
    aspect-ratio: 1 / 1;
    border-radius: 0.5rem;
    overflow: hidden;
    border: 3px solid #fff;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;

    @media (min-aspect-ratio: 4 / 5) and (max-aspect-ratio: 17 / 10) {
        max-width: 450px;
    }

    @media (min-aspect-ratio: 2 / 1) {
        max-width: 450px;
    }

    @media screen and (min-width: 1200px) and (max-width: 1440px) {
        max-width: 500px;
    }
}
</style>
